import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Check, ArrowRight } from "lucide-react";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { waLink } from "@/constants/site";
import { useAuth } from "@/context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => { setP(data); track("product_view", { id, name: data.name }); }).catch(() => {});
  }, [id]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#C9A45C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12" data-testid="product-detail-page">
      <Seo title={p.name} description={p.description} />
      <Link to="/products" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"><ArrowLeft size={16} /> Back to products</Link>

      <div className="mt-8 grid lg:grid-cols-2 gap-10">
        <div className="aspect-[4/3] overflow-hidden hair-border">
          <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
        </div>
        <div>
          <span className="errah-label gold-text">{p.subcategory} · {p.protection_level}</span>
          <h1 className="font-display text-4xl lg:text-5xl font-light tracking-tight mt-2">{p.name}</h1>
          {p.price_from && <p className="mt-4 text-2xl gold-text">From ₹{p.price_from}<span className="text-gray-500 text-sm">/{p.price_unit}</span></p>}
          <p className="mt-6 text-gray-400 leading-relaxed">{p.description}</p>

          {p.suitable_surfaces?.length > 0 && (
            <div className="mt-8">
              <p className="errah-label mb-3">Suitable surfaces</p>
              <div className="flex flex-wrap gap-2">
                {p.suitable_surfaces.map((s) => <span key={s} className="px-3 py-1 text-xs border border-white/15 text-gray-300">{s}</span>)}
              </div>
            </div>
          )}
          {p.benefits?.length > 0 && (
            <div className="mt-8">
              <p className="errah-label mb-3">Benefits</p>
              <ul className="grid grid-cols-2 gap-2">
                {p.benefits.map((b) => <li key={b} className="text-sm text-gray-300 flex items-center gap-2"><Check size={14} className="text-[#C9A45C]" />{b}</li>)}
              </ul>
            </div>
          )}
          {p.application && <p className="mt-8 text-sm text-gray-400"><span className="errah-label">Application</span><br />{p.application}</p>}

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate("/quote", { state: { product_id: p.id, surface_type: p.suitable_surfaces?.[0], preferred_service: p.name } })}
              data-testid="pd-quote-btn" className="inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black px-6 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
              Request Quote
            </button>
            <button onClick={() => navigate(user ? "/booking" : "/login", { state: { product_id: p.id, service: p.name } })}
              data-testid="pd-book-btn" className="inline-flex items-center justify-center gap-2 border border-white/20 px-6 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
              Book Installation <ArrowRight size={15} />
            </button>
            <a href={waLink(`Hi ERRAH, I'm interested in ${p.name}.`)} target="_blank" rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { location: "product", id: p.id })} data-testid="pd-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 text-sm text-[#25D366] px-4 py-3.5 hover:underline">
              Enquire on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
